import {Injectable, OnDestroy} from '@angular/core';
import {ActivatedRouteSnapshot, ResolveEnd, Router} from '@angular/router';
import {ApplicationInsights} from '@microsoft/applicationinsights-web';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {environment} from '../../../environments/environment';

/**
 * Application Insights integration for telemetry and page view tracking.
 *
 * Listens to router resolve events to log page views and exposes helpers
 * for setting/clearing authenticated user context and manual page view logging.
 */
@Injectable({
  providedIn: 'root',
})
export class ApplicationInsightsService implements OnDestroy {

  /** App Insights SDK instance when configured. */
  appInsights: ApplicationInsights;
  /** Subscription to router events used for page view logging. */
  private readonly routerSubscription: Subscription;

  /**
   * Initializes Application Insights if an instrumentation key is present.
   * @param router Angular router to observe navigation lifecycle.
   */
  constructor(
    private router: Router,
  ) {
    if (environment.appInsights && environment.appInsights.instrumentationKey) {
      this.appInsights = new ApplicationInsights({
        config: {
          instrumentationKey: environment.appInsights.instrumentationKey,
          autoTrackPageVisitTime: true,
        },
      });
      this.appInsights.loadAppInsights();

      this.routerSubscription = this.router.events
        .pipe(
          filter((event) => event instanceof ResolveEnd),
        )
        .subscribe((event: ResolveEnd) => {
          const activatedComponent = this.getActivatedComponent(event.state.root);
          if (activatedComponent) {
            this.logPageView(`${activatedComponent.name} ${this.getRouteTemplate(event.state.root)}`, event.urlAfterRedirects);
          }
        });
    }
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Associates telemetry with an authenticated user id.
   */
  setUserId(userId: string) {
    this.appInsights?.setAuthenticatedUserContext(userId);
  }

  /**
   * Clears the authenticated user id from telemetry context.
   */
  clearUserId() {
    this.appInsights?.clearAuthenticatedUserContext();
  }

  /**
   * Tracks a page view event.
   * @param name Optional page name.
   * @param uri Optional page URI.
   */
  logPageView(name?: string, uri?: string) {
    this.appInsights?.trackPageView({name, uri});
  }

  /**
   * Traverses the activated route tree to find the concrete component.
   */
  private getActivatedComponent(snapshot: ActivatedRouteSnapshot): any {
    if (snapshot.firstChild) {
      return this.getActivatedComponent(snapshot.firstChild);
    }

    return snapshot.component;
  }

  /**
   * Builds a route template string from the activated route tree.
   */
  private getRouteTemplate(snapshot: ActivatedRouteSnapshot): string {
    let path = '';
    if (snapshot.routeConfig) {
      path += snapshot.routeConfig.path;
    }

    if (snapshot.firstChild) {
      return path + this.getRouteTemplate(snapshot.firstChild);
    }

    return path;
  }

}
